import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, BookOpen, Zap, Moon, ChevronRight, BarChart2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import { useNavigate } from 'react-router-dom';

interface WeeklyStats {
  journal_entries: number;
  coach_actions: number;
  avg_stress: number;
  avg_sleep: number;
}

function generateInsight(stats: WeeklyStats, stressTrend: 'up' | 'down' | 'flat'): string {
  if (stressTrend === 'down' && stats.journal_entries >= 3) {
    return "Great week - your journaling is helping reduce stress";
  }
  if (stats.coach_actions >= 5) {
    return "You're crushing it with your action items this week";
  }
  if (stats.journal_entries >= 5) {
    return "Consistent journaling - you're building a strong habit";
  }
  if (stressTrend === 'up') {
    return "Stress is up - consider a coaching session";
  }
  if (stats.avg_sleep >= 420) {
    return "Solid sleep this week - keep it up";
  }
  return "Keep showing up - every entry counts";
}

export function ScorecardSummary() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<WeeklyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [stressTrend, setStressTrend] = useState<'up' | 'down' | 'flat'>('flat');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    loadStats();
    setMounted(true);
  }, []);

  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  async function loadStats() {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const weekStart = getWeekStart(new Date());
      const weekStartStr = weekStart.toISOString().split('T')[0];

      const { data: weeklyData } = await supabase
        .rpc('get_weekly_journal_stats', {
          p_user_id: user.id,
          p_week_start: weekStartStr
        });

      const { data: coachActions } = await supabase
        .rpc('get_weekly_coach_actions', {
          p_user_id: user.id,
          p_week_start: weekStartStr
        });

      const prevWeekStart = new Date(weekStart);
      prevWeekStart.setDate(prevWeekStart.getDate() - 7);
      const prevWeekStartStr = prevWeekStart.toISOString().split('T')[0];

      const { data: prevWeekData } = await supabase
        .rpc('get_weekly_journal_stats', {
          p_user_id: user.id,
          p_week_start: prevWeekStartStr
        });

      const currentWeekData = weeklyData?.[0] || {
        entry_count: 0,
        avg_stress: 5,
        avg_sleep: 0
      };

      const prevWeekStress = prevWeekData?.[0]?.avg_stress || 5;
      const stressDelta = currentWeekData.avg_stress - prevWeekStress;

      if (stressDelta > 0.5) setStressTrend('up');
      else if (stressDelta < -0.5) setStressTrend('down');
      else setStressTrend('flat');

      setStats({
        journal_entries: currentWeekData.entry_count || 0,
        coach_actions: coachActions || 0,
        avg_stress: currentWeekData.avg_stress || 5,
        avg_sleep: currentWeekData.avg_sleep || 0
      });

    } catch (err) {
      logger.error('Error loading scorecard stats:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
        <div className="flex justify-center py-6">
          <div className="relative">
            <div className="w-8 h-8 rounded-full border-2 border-gray-100" />
            <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!stats || stats.journal_entries === 0) {
    return null;
  }

  const sleepHours = Math.round(stats.avg_sleep / 60 * 10) / 10;
  const insight = generateInsight(stats, stressTrend);

  const statItems = [
    {
      icon: BookOpen,
      value: stats.journal_entries,
      label: 'entries',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      trend: null
    },
    {
      icon: Zap,
      value: stats.coach_actions,
      label: 'actions',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      trend: null
    },
    {
      icon: stressTrend === 'down' ? TrendingDown : stressTrend === 'up' ? TrendingUp : BarChart2,
      value: stats.avg_stress.toFixed(1),
      label: 'stress',
      bgColor: stressTrend === 'down' ? 'bg-green-50' : stressTrend === 'up' ? 'bg-rose-50' : 'bg-gray-50',
      iconColor: stressTrend === 'down' ? 'text-green-600' : stressTrend === 'up' ? 'text-rose-600' : 'text-gray-500',
      trend: stressTrend
    },
    {
      icon: Moon,
      value: sleepHours,
      label: 'hrs sleep',
      bgColor: 'bg-sky-50',
      iconColor: 'text-sky-600',
      trend: null
    }
  ];

  return (
    <button
      onClick={() => navigate('/scorecard')}
      className={`w-full bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden hover:shadow-md transition-all duration-300 group text-left ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl flex items-center justify-center">
              <BarChart2 className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">This Week</h3>
              <p className="text-sm text-gray-500">Your progress at a glance</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4">
          {statItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className={`text-center p-3 rounded-xl ${item.bgColor} transition-all duration-300 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                style={{ transitionDelay: `${100 + index * 50}ms` }}
              >
                <div className="flex justify-center mb-2">
                  <Icon className={`h-5 w-5 ${item.iconColor}`} />
                </div>
                <p className="text-xl font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            );
          })}
        </div>

        <div className={`flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl transition-all duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '350ms' }}>
          <div className={`w-2 h-2 rounded-full ${
            stressTrend === 'down' ? 'bg-green-500' : stressTrend === 'up' ? 'bg-rose-500' : 'bg-amber-500'
          }`} />
          <p className="text-sm text-gray-600">{insight}</p>
        </div>
      </div>
    </button>
  );
}
